import React, { useMemo, useState } from 'react';
import {  Grid, IconButton,MenuItem, SelectChangeEvent, TextField } from '@mui/material';
import { DeleteOutlined } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { isEqual } from 'underscore';
import { DateTimePicker } from '@mui/x-date-pickers';
import { format, isValid, parseISO } from 'date-fns';
import { makeStyles } from '@mui/styles';

import { SelectField } from '../Common/Fields';
import { paddings } from '../Common/styles/spacing';
import { dispatch_theme } from '@/config/colors';
import { setHistoryFilter } from '@/actions/plannings/pure';
import { ColumnKey, columns, Filter, PlanningColumn } from '../UserManagement/PlanningTable';
import { RootState } from '../../index';

const useStyles = makeStyles((theme: typeof dispatch_theme) => ({
  ...paddings.left,
  ...paddings.right
}));

export type Operator = '=' | '!=' | '>' | '<' | '>=' | '<=';
export const Operations: Operator[] = ['=', '!=', '>', '<', '>=', '<='];

type Props = {
  column: PlanningColumn,
  onChange: (id: ColumnKey,  op: Operator, value: any) => void,
  initialFilter: {
    id: ColumnKey,
    op: Operator,
    value: any,
  },
}

const TableFilter = ({column, onChange}: Props) => {
  const {history} = useSelector((state: RootState) => state.planning, isEqual);
  const dispatch = useDispatch();
  const onSetFilter = ((filters: Filter[]) => dispatch(setHistoryFilter(filters)));
  const [filter, setFilter] = useState<{op: Operator, value: any, id: ColumnKey}>({
    id: column.id,
    op: getInitialOperator(column.id, history.filters),
    value: getInitValue(column.id, history.filters)
  });
  
  const classes = useStyles();

  const columnsToDisplay = useMemo(() => columns().filter((c) => c.id !== 'state' && c.id !== 'download'), []);

  function getInitialOperator(colKey: ColumnKey, filters: Filter[]): any {
    const storedValue = filters.find((f: Filter) => f.id === colKey);
    if(storedValue?.op){
      return storedValue.op;
    }
    return '=';
  }

  function getInitValue(colKey: ColumnKey, filters: Filter[]): any {
    const storedValue = filters.find((f: Filter) => f.id === colKey);
    if(storedValue?.value){
      return storedValue.value;
    }
    switch (colKey) {
    case 'timestamp':
    default:
      return '';
    }
  }

  const handleChange = (value: any, id: ColumnKey) => {
    const newFilter: Filter = {...filter, value};
    const existingFilterIndex: number = history.filters.findIndex((f: Filter) => f.id === id);
    const existingFilter: Filter = history.filters.find((f: Filter) => f.id === id);
    if(!value) return resetColumnFilter();
    if(isEqual(value, existingFilter?.value)) return;
    const newFilters = [...history.filters];
    if(existingFilterIndex !== -1) {
      newFilters[existingFilterIndex] = newFilter;
    } else {
      newFilters.push(newFilter);
    }
    setFilter(newFilter);
    onSetFilter(newFilters);
    if(column.id === newFilter.id) {
      onChange(column.id, filter.op, newFilter.value);
    }
  };

  const handleDateChange = (value: any, id: ColumnKey, withHours = false) => {
    if(!value) return resetColumnFilter();
    if(!isValid(value)) return;
    const formatting = withHours ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd';
    const formatted = format(value, formatting);
    handleChange(parseISO(formatted), id);
  };
  
  const handleOperatorChange = (event: SelectChangeEvent) => {
    const newFilter = {
      ...filter,
      op: event.target.value as Operator,
    };
    setFilter(newFilter);

    const existingFilterIndex: number = history.filters.findIndex((f: Filter) => f.id === newFilter.id);
    if(existingFilterIndex !== -1) {
      const newFilters = [...history.filters];
      newFilters[existingFilterIndex] = newFilter;
      onSetFilter(newFilters);
    } else {
      onSetFilter([...history.filters, newFilter]);
    }
    if(column.id === newFilter.id) {
      onChange(column.id, newFilter.op, newFilter.value);
    }
  };

  const initialFilter = (id: ColumnKey, filters: Filter[]): Filter => ({
    id: id,
    op: getInitialOperator(id, filters),
    value: getInitValue(id, filters)
  });

  const handleColumnChange = (event: SelectChangeEvent) => {
    const newFilter = initialFilter(event.target.value as ColumnKey, history.filters);
    setFilter(newFilter);
  };

  const resetColumnFilter = () => {
    const {id} = filter;
    const existingFilterIndex: number = history.filters.findIndex((f: Filter) => f.id === id);
    if(existingFilterIndex !== -1) {
      const newFilters = [...history.filters];
      newFilters.splice(existingFilterIndex, 1);
      onSetFilter(newFilters);
      setFilter(initialFilter(column.id, newFilters));
    } else setFilter(initialFilter(column.id, history.filters));

  };

  const ColumnSelectField = (
    <SelectField
      value={filter.id}
      name={'column'}
      variant={'outlined'}
      margin={'none'}
      label={'Column'}
      onSelect={handleColumnChange}
      items={columnsToDisplay.map((col: PlanningColumn) => (
        <MenuItem key={col.id} value={col.id}>
          {`${col.label}`}
        </MenuItem>
      ))}
    />
  );

  const OperatorSelectField = (
    <SelectField
      value={filter.op}
      name={'operator'}
      variant={'outlined'}
      margin={'none'}
      label={'Operator'}
      onSelect={handleOperatorChange}
      items={Operations.map((op: Operator) => (
        <MenuItem key={op} value={op}>
          {op}
        </MenuItem>
      ))}
    />
  );

  const ResetFilterButton = (
    <IconButton size={'small'} onClick={resetColumnFilter}>
      <DeleteOutlined />
    </IconButton>
  );

  switch(filter.id) {
  case 'timestamp':
    return (
      <Grid item container spacing={1} xs={12} alignItems={'center'} className={`${classes.pl_1} ${classes.pr_1}`}>
        <Grid item>
          {ResetFilterButton}
        </Grid>
        <Grid item>
          {ColumnSelectField}
        </Grid>
        <Grid item sx={{minWidth:80}}>
          {OperatorSelectField}
        </Grid>
        <Grid item>
          <DateTimePicker
            format={'dd/MM/yyyy kk:mm'}
            slotProps={{
              textField: {
                sx:{width:190}
              }
            }}
            label="Date Time"
            value={filter.value}
            onChange={(newValue: any) => {
              handleDateChange(newValue, filter.id, true);
            }}
          />
        </Grid>
      </Grid>
    );
  case 'optimization_id':
    return (
      <Grid item container spacing={1} xs={12} alignItems={'center'} className={`${classes.pl_1} ${classes.pr_1}`}>
        <Grid item>
          {ResetFilterButton}
        </Grid>
        <Grid item>
          {ColumnSelectField}
        </Grid>
        <Grid item>
          <TextField
            type={'text'}
            fullWidth
            label={columnsToDisplay.find(c => c.id === filter.id)?.label}
            value={filter.value}
            onChange={(e) => {
              handleChange(e.target.value, filter.id);
            }}
          />
        </Grid>
      </Grid>);
  default:
    return null;
  }

};

export default TableFilter;